import { Box, Link, Stack, Typography } from '@mui/material';

import {
    ensureAbsoluteUrl,
    formatDate,
    shipmentMonitoringCellTypographySx,
    shipmentMonitoringStackCellRootSx,
    formatAirAliasQuantityWithUnit,
} from './shipmentMonitoringDefShared';
import { shipmentMonitoringClosureColumns } from './shipmentMonitoringClosureColumns';
import { getShipmentMonitoringIlStatusColumn } from './shipmentMonitoringIlStatusColumn';

export const getShipmentMonitoringAirExportColumns = (rows) => {
    return [
        {
            field: 'il_opus_reference_id',
            headerName: 'OPUS#',
            width: 75,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.value || p.row.il_opus_reference_id}
                    </Typography>
                );
            },
        },
        {
            field: 'il_trade_partner_id_client',
            headerName: 'Client',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.value || p.row.il_trade_partner_id_client}
                    </Typography>
                );
            },
        },
        {
            field: 'il_trade_partner_id_shipper',
            headerName: 'Shipper',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_air_export_hbl ?? []).map((hbl) => hbl.il_trade_partner_id_shipper).join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'il_trade_partner_id_consignee',
            headerName: 'Consignee',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_air_export_hbl ?? [])
                    .map((hbl) => hbl.il_trade_partner_id_consignee /* 필요 시 .name 등 */)
                    .join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'mawb',
            headerName: 'MAWB#',
            width: 105,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => <Typography sx={shipmentMonitoringCellTypographySx}>{p.value ?? ''}</Typography>,
        },
        {
            field: 'hawb',
            headerName: 'HAWB#',
            width: 95,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_air_export_hbl ?? []).map((hbl, i) => {
                        const text = hbl.hawb ?? '';
                        const href = (hbl.hawb_link && String(hbl.hawb_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={hbl.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || 'View'}
                            </Link>
                        ) : (
                            <Typography key={hbl.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'alias',
            headerName: 'Alias#',
            width: 75,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_air_export_hbl_alias ?? []).map((hbl) => hbl.alias).join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'il_opus_vessel_id',
            headerName: 'Flight#',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_air_export_hbl_alias ?? []).map((hbl) => hbl.il_opus_vessel_id).join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'volume',
            headerName: 'Vol',
            width: 72,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_air_export_hbl_alias ?? [])
                    .map((alias) => formatAirAliasQuantityWithUnit(alias))
                    .filter(Boolean)
                    .join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines || ' '}</Typography>;
            },
        },
        {
            field: 'place_of_receipt',
            headerName: 'POR',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>{p.value || p.row.place_of_receipt}</Typography>
                );
            },
        },
        {
            field: 'il_location_id_aol',
            headerName: 'AOL',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.value?.code || p.row.il_location_id_aol?.code}
                    </Typography>
                );
            },
        },

        {
            field: 'il_location_id_aod',
            headerName: 'AOD',
            width: 80,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.value?.code || p.row.il_location_id_aod?.code}
                    </Typography>
                );
            },
        },
        {
            field: 'il_location_id_faod',
            headerName: 'F. DEST',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.value?.code || p.row.il_location_id_faod?.ncodeame}
                    </Typography>
                );
            },
        },
        {
            field: 'doc_cut',
            headerName: 'DOC CUT',
            width: 50,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        DOC
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        CUT
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                return <Typography sx={shipmentMonitoringCellTypographySx}>{formatDate(p.row.etd) || ' '}</Typography>;
            },
        },
        {
            field: 'port_cut',
            headerName: 'PORT CUT',
            width: 50,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        PORT
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        CUT
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                return <Typography sx={shipmentMonitoringCellTypographySx}>{formatDate(p.row.etd) || ' '}</Typography>;
            },
        },
        {
            field: 'etd',
            headerName: 'ETD',
            width: 50,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return <Typography sx={shipmentMonitoringCellTypographySx}>{formatDate(p.row.etd) || ' '}</Typography>;
            },
        },
        {
            field: 'eta',
            headerName: 'ETA',
            width: 50,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return <Typography sx={shipmentMonitoringCellTypographySx}>{formatDate(p.row.eta) || ' '}</Typography>;
            },
        },
        {
            field: 'feta',
            headerName: 'F.ETA',
            width: 50,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return <Typography sx={shipmentMonitoringCellTypographySx}>{formatDate(p.row.feta) || ' '}</Typography>;
            },
        },
        {
            field: 'booking_confirmation',
            headerName: 'Booking Confirmation',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Booking
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Confirmation
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.row.booking_confirmation ?? ''} <br />
                        {p.row.booking_confirmation_date ? formatDate(p.row.booking_confirmation_date) : ''}
                    </Typography>
                );
            },
        },
        {
            field: 'aes_itn',
            headerName: 'AES ITN#',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.row.aes_itn ?? ''} <br />
                        {p.row.aes_itn_number ?? ''} <br />
                        {p.row.aes_itn_date ? formatDate(p.row.aes_itn_date) : ''}
                    </Typography>
                );
            },
        },
        {
            field: 'delivery_order',
            headerName: 'Delivery Order',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Delivery
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Order
                    </Box>
                </Box>
            ),
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_air_export_hbl_alias ?? []).map((cntr, i) => {
                        const text = cntr.delivery_order ?? '';
                        const href = (cntr.delivery_order_link && String(cntr.delivery_order_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={cntr.id ?? i}
                                href={absoluteUrl}
                                component="a"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || 'View'}
                            </Link>
                        ) : (
                            <Typography key={cntr.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'trucker',
            headerName: 'Trucker',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Stack sx={shipmentMonitoringStackCellRootSx}>
                        {(p.row.il_air_export_hbl_alias ?? []).map((cntr, i) => {
                            const text = cntr.il_trade_partner_id ?? '';
                            return (
                                <Typography key={cntr.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                    {text}
                                </Typography>
                            );
                        })}
                    </Stack>
                );
            },
        },
        getShipmentMonitoringIlStatusColumn({ statusHouseHeaderStyle: 'hawb' }),
        {
            field: 'dg_hazmat',
            headerName: 'DG/HAZMAT',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        DG
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        HAZMAT
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                return (
                    <Link
                        key={p.row.id}
                        href={p.row.dg_hazmat_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        component="a"
                        sx={{
                            ...shipmentMonitoringCellTypographySx,
                            color: 'primary.main',
                            textDecoration: 'underline',
                        }}
                    >
                        {p.row.dg_hazmat ? p.row.dg_hazmat : ''} <br />
                        {p.row.dg_hazmat_date ? formatDate(p.row.dg_hazmat_date) : ''}
                    </Link>
                );
            },
        },
        {
            field: 'shipping_instruction',
            headerName: 'Shipping Instruction',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Shipping
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Instruction
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.row.shipping_instruction ?? ''} <br />
                        {p.row.shipping_instruction_date ? formatDate(p.row.shipping_instruction_date) : ''}
                    </Typography>
                );
                return <Typography sx={shipmentMonitoringCellTypographySx}>{p.row.aes_itn_number ?? ''}</Typography>;
            },
        },
        {
            field: 'stages',
            headerName: 'Stages',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Stack sx={shipmentMonitoringStackCellRootSx}>
                        {(p.row.il_air_export_hbl_alias ?? []).map((cntr, i) => {
                            const text = cntr.stages ?? '';
                            const date = cntr.stages_date ?? '';
                            return (
                                <Typography key={cntr.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                    {text}
                                    <br />
                                    {date ? formatDate(date) : ''}
                                </Typography>
                            );
                        })}
                    </Stack>
                );
            },
        },
        {
            field: 'air_freight',
            headerName: 'Collect Charge',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Collect
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Charge
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                return (
                    <Link
                        key={p.row.id}
                        href={p.row.ocean_freight_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        component="a"
                        sx={{
                            ...shipmentMonitoringCellTypographySx,
                            color: 'primary.main',
                            textDecoration: 'underline',
                        }}
                    >
                        {p.row.ocean_freight ? p.row.ocean_freight : ''} <br />
                        {p.row.paid_on ? formatDate(p.row.paid_on) : ''}
                    </Link>
                );
            },
        },
        {
            field: 'pre_alert',
            headerName: 'Pre Alert',
            width: 50,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Pre
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Alert
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                const lines = (p.row.il_air_export_hbl ?? []).map((hbl) => hbl.pre_alert).join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'invoice',
            headerName: 'Invoice',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_air_export_hbl ?? []).map((hbl, i) => {
                        const text = hbl.invoice ?? '';
                        const date = hbl.invoice_date ?? '';
                        const href = (hbl.invoice_link && String(hbl.invoice_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={hbl.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Link>
                        ) : (
                            <Typography key={hbl.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'crdr',
            headerName: 'CR/DR Note',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        CR/DR
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Note
                    </Box>
                </Box>
            ),
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_air_export_hbl ?? []).map((hbl, i) => {
                        const text = hbl.crdr ?? '';
                        const date = hbl.crdr_date ?? '';
                        const href = (hbl.crdr_link && String(hbl.crdr_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={hbl.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Link>
                        ) : (
                            <Typography key={hbl.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        ...shipmentMonitoringClosureColumns,
    ];
};
